import { useTheme } from "@/hooks/useTheme";

const problems = [
  {
    icon: "ri-phone-fill",
    text: "Missed calls turn into lost revenue",
    detail: "Every unanswered call is a potential deal walking out the door — permanently.",
  },
  {
    icon: "ri-timer-2-line",
    text: "Slow follow-ups reduce conversion rates",
    detail: "Speed-to-lead is critical. Delays of even hours can cost you the deal to a faster competitor.",
  },
  {
    icon: "ri-loop-right-line",
    text: "Manual processes create bottlenecks",
    detail: "When humans are required for every step, your scale is limited by your headcount.",
  },
  {
    icon: "ri-eye-off-line",
    text: "Lack of visibility leads to inconsistent growth",
    detail: "Without real-time data on your pipeline, you can&apos;t make informed decisions — only reactive ones.",
  },
];

export default function ProblemSection() {
  const { isDark } = useTheme();

  const bg = isDark ? "#0d0818" : "#f0e9de";
  const cardBg = isDark ? "#1a0d28" : "white";
  const cardBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const headingColor = isDark ? "#e4d0f8" : "#4d316c";
  const subheadingColor = isDark ? "#c4aad8" : "#705a89";
  const bodyColor = isDark ? "rgba(255,255,255,0.58)" : "#7a6b80";
  const labelColor = isDark ? "#a9baab" : "#869b8b";
  const iconBg = isDark ? "rgba(169,186,171,0.12)" : "#f0e9de";
  const imageBorder = isDark ? "rgba(255,255,255,0.09)" : "#e4d8c8";
  const statCardBg = isDark ? "#1a0d28" : "white";
  const statCardDarkBg = isDark ? "#2c1a40" : "#4d316c";

  return (
    <section id="problem" className="py-28 md:py-40 overflow-hidden transition-colors duration-300" style={{ background: bg }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Top label */}
        <div className="text-center mb-20">
          <span className="inline-block text-xs font-semibold tracking-widest uppercase mb-5" style={{ color: labelColor }}>
            The Real Problem
          </span>
          <h2 className="text-4xl md:text-6xl lg:text-7xl font-semibold leading-tight tracking-tight mb-5" style={{ color: headingColor }}>
            Most Businesses Don&apos;t Have
            <br className="hidden md:block" /> a Lead Problem.
          </h2>
          <p className="text-xl md:text-2xl font-medium" style={{ color: subheadingColor }}>
            They Have a System Problem.
          </p>
        </div>

        {/* Split layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Problem cards */}
          <div className="flex flex-col gap-4">
            {problems.map((prob) => (
              <div
                key={prob.text}
                className="flex gap-5 p-6 rounded-2xl transition-all duration-300"
                style={{ background: cardBg, border: `1px solid ${cardBorder}` }}
              >
                <div
                  className="flex-shrink-0 w-11 h-11 flex items-center justify-center rounded-xl"
                  style={{ background: iconBg }}
                >
                  <i className={`${prob.icon} text-xl`} style={{ color: isDark ? "#c4aad8" : "#4d316c" }}></i>
                </div>
                <div>
                  <p className="font-semibold text-sm md:text-base mb-1" style={{ color: headingColor }}>
                    {prob.text}
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: bodyColor }}>
                    {prob.detail}
                  </p>
                </div>
              </div>
            ))}

            {/* Quote card */}
            <div
              className="p-7 rounded-2xl mt-2"
              style={{ background: "linear-gradient(135deg, #4d316c 0%, #705a89 100%)" }}
            >
              <i className="ri-double-quotes-l text-2xl mb-3 block" style={{ color: "rgba(196,170,216,0.5)" }}></i>
              <p className="text-white/85 text-base leading-relaxed font-medium">
                The companies that scale predictably aren&apos;t the ones with the best product — they&apos;re the ones with the most reliable systems.
              </p>
              <p className="mt-4 text-xs font-semibold tracking-widest uppercase" style={{ color: "#c4aad8" }}>
                Patronum AI Philosophy
              </p>
            </div>
          </div>

          {/* Right: Visual */}
          <div className="relative">
            <div className="relative w-full rounded-3xl overflow-hidden" style={{ border: `1px solid ${imageBorder}` }}>
              <img
                src="https://public.readdy.ai/ai/img_res/f72aac1321dabcefcdd44253cda12ccc.jpg"
                alt="Broken systems vs. organized operations"
                className="w-full h-auto object-cover"
                style={{ maxHeight: "580px" }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#4d316c]/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <p className="text-white font-semibold text-lg leading-snug">
                  Broken systems cost more than broken deals.
                </p>
                <p className="text-white/60 text-sm mt-1">
                  Every operational gap compounds over time.
                </p>
              </div>
            </div>

            {/* Floating stat */}
            <div
              className="absolute -top-5 -right-5 px-5 py-4 rounded-2xl"
              style={{ background: statCardBg, border: `1px solid ${cardBorder}` }}
            >
              <div className="text-2xl font-semibold" style={{ color: headingColor }}>72%</div>
              <div className="text-xs mt-0.5" style={{ color: labelColor }}>of leads lost to slow response</div>
            </div>

            {/* Floating stat 2 */}
            <div
              className="absolute -bottom-5 -left-5 px-5 py-4 rounded-2xl"
              style={{ background: statCardDarkBg, border: "1px solid rgba(255,255,255,0.1)" }}
            >
              <div className="text-2xl font-semibold text-white">48hrs</div>
              <div className="text-xs mt-0.5" style={{ color: "#a9baab" }}>avg. industry response time</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
